import type { Document } from '@contentful/rich-text-types';
import React from 'react';
import Image from 'next/legacy/image';
import classNames from 'classnames';
import type { CmsLink, CmsShortText } from '$cms/types';
import { isCmsLink, isCmsShortText } from '$cms/types';
import { HeadingLevels } from '$src-components/atoms/HeadingLevels';
import { GoogleReviewsSummary } from '$quantum/molecules/GoogleReviewsSummary';
import { ReviewProvider } from '$constants/review-providers';
import { TrustPilotReviewManual } from '$quantum/molecules/TrustPilotReviewManual';
import { renderRichTextContent } from '@/cms/richTextContentHelpers';
import { Button } from '@/components/atoms/Button';
import { renderComparisonCardContentOptions } from './helpers';

import styles from './index.module.scss';

export interface ComparisonCardProps {
  readonly title: string;
  readonly titleTag?: string;
  readonly subtitle: string;
  readonly subtitlePrice: string;
  readonly ctaText: string;
  readonly ctaHandler: () => void;
  readonly metaTitle?: string;
  readonly metaSubTitle?: CmsLink | CmsShortText;
  readonly content: Document;
  readonly reviewProvider: ReviewProvider;
  readonly rating: number;
}

const googleLogo = {
  url: 'https://res.cloudinary.com/fxtr/image/upload/v1664805750/google_gkppju.png',
  width: 66,
  height: 24,
  key: 'google_logo',
  alt: 'Google logo',
};

/**
 * A component that displays useful information, which could be used to<br />
 * compare different options
 */
export function ComparisonCard({
  title,
  titleTag,
  subtitle,
  subtitlePrice,
  ctaText,
  ctaHandler,
  content,
  metaSubTitle,
  metaTitle,
  reviewProvider,
  rating,
}: ComparisonCardProps): JSX.Element {
  return (
    <div
      className={classNames(
        ComparisonCard.displayName,
        { [styles.WithTag]: Boolean(titleTag) },
        styles.ComparisonCard
      )}
    >
      <div className={styles.InnerWrapper}>
        {titleTag && (
          <div className={styles.TitleTagWrapper}>
            <p className={styles.TitleTag}>{titleTag}</p>
          </div>
        )}
        <HeadingLevels semanticLevel={3} className={styles.Title}>
          {title}
        </HeadingLevels>
        <div className={styles.PriceWrapper}>
          <p className={classNames('h6', styles.Subtitle)}>{subtitle}</p>
          <p className={styles.Price}>{subtitlePrice}</p>
        </div>
        <Button fullWidth size="L" onClick={ctaHandler}>
          {ctaText}
        </Button>
        {(metaSubTitle || metaTitle) && (
          <div className={styles.MetaWrapper}>
            <div className={styles.MetaIcon}>
              <Image src="/img/svg-icons/clipboard.svg" alt="clipboard-icon" width={64} height={64} />
            </div>
            <div className={classNames(styles.MetaTextWrapper)}>
              <p className={styles.MetaTitle}>{metaTitle}</p>
              {isCmsLink(metaSubTitle) && <a href={metaSubTitle.url}>{metaSubTitle.text}</a>}
              {isCmsShortText(metaSubTitle) && (
                <p className={styles.MetaSubtitleText}>{metaSubTitle.value}</p>
              )}
            </div>
          </div>
        )}
        <div className={styles.Content}>
          {renderRichTextContent(content, renderComparisonCardContentOptions)}
        </div>
        <div className={styles.Review}>
          {reviewProvider === ReviewProvider.GOOGLE && (
            <GoogleReviewsSummary
              rating={rating}
              providerLogo={googleLogo}
              providerLogoWidth={66}
              providerLogoHeight={24}
            />
          )}
          {reviewProvider === ReviewProvider.TRUSTPILOT && <TrustPilotReviewManual rating={rating} />}
        </div>
      </div>
    </div>
  );
}
ComparisonCard.displayName = 'ComparisonCard';
